
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { usePatientData } from "@/hooks/usePatientData";
import { PatientSearchInput } from "./PatientSearchInput";
import { PatientTable } from "./PatientTable";
import { PatientListLoading } from "./PatientListLoading";
import { PatientListEmpty } from "./PatientListEmpty";

interface PatientListProps {
  clinicId: string;
}

export function PatientList({ clinicId }: PatientListProps) {
  console.log("🏥 PatientList component rendering");
  console.log("🏥 Received clinicId:", clinicId, typeof clinicId);
  
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [debugCounter, setDebugCounter] = useState(0);

  // Force re-render every 10 seconds during debugging
  useEffect(() => {
    const timer = setInterval(() => {
      setDebugCounter(prev => prev + 1);
      console.log("🔄 Debug re-render counter:", debugCounter + 1);
    }, 10000);
    
    return () => clearInterval(timer);
  }, [debugCounter]);

  const { data: patients, isLoading, error } = usePatientData(clinicId, searchQuery);
  
  useEffect(() => {
    console.log("PatientList - Data changed:");
    console.log("- clinicId:", clinicId);
    console.log("- searchQuery:", searchQuery);
    console.log("- patients data:", patients);
    console.log("- patients count:", patients?.length);
    console.log("- isLoading:", isLoading);
    console.log("- error:", error);
    
    // Check for common data issues
    if (patients && patients.length === 0) {
      console.log("⚠️ Empty patients array returned from hook");
    } else if (!patients && !isLoading) {
      console.log("⚠️ Patients data is null/undefined but not loading");
    } else if (patients) {
      // Check if patients have expected structure and data
      const examplePatient = patients[0];
      console.log("Example patient structure:", examplePatient);
      
      // Check if any critical fields are missing
      const missingFields = [];
      ['id', 'first_name', 'last_name', 'email'].forEach(field => {
        if (!examplePatient[field]) missingFields.push(field);
      });
      
      if (missingFields.length > 0) {
        console.warn(`⚠️ Patient missing critical fields: ${missingFields.join(', ')}`);
      }
    }
  }, [clinicId, searchQuery, patients, isLoading, error]);

  const handleViewPatient = (patientId: string) => {
    console.log("🔍 Navigating to patient detail page for ID:", patientId);
    navigate(`/patients/${patientId}`);
  };

  if (isLoading) {
    console.log("⏳ Rendering loading state...");
    return <PatientListLoading />;
  }
  
  if (error) {
    console.error("❌ Error fetching patient data:", error);
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h3 className="text-lg font-medium text-red-600">Error loading patients</h3>
        <p className="text-sm text-red-500">Please try refreshing the page.</p>
        <p className="text-xs text-gray-500 mt-2">Technical details: {error.message}</p>
      </div>
    );
  }

  // Check if we should show empty state
  const showEmptyState = !patients || patients.length === 0;
  console.log("🖥️ Rendering patient list, showEmptyState:", showEmptyState);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Patients ({patients?.length || 0})
            
          </CardTitle>
          <PatientSearchInput 
            searchQuery={searchQuery}
            onSearchChange={(query) => {
              console.log("🔍 Search query changed to:", query);
              setSearchQuery(query);
            }}
          />
        </div>
      </CardHeader>
      <CardContent>
        {showEmptyState ? (
          <>
            <div className="bg-yellow-50 p-2 mb-4 border border-yellow-200 rounded text-sm">
              Debug info: clinicId={clinicId}, data status={patients ? "empty array" : "null/undefined"}
            </div>
            <PatientListEmpty />
          </>
        ) : (
          <PatientTable 
            key={`patient-table-${patients.length}-${searchQuery}-${debugCounter}`}
            patients={patients}
            onViewPatient={handleViewPatient}
          />
        )}
      </CardContent>
    </Card>
  );
}
